import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ChevronRight } from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import { useDemoStatus } from "../hooks/useDemo";
import type { DemoUser } from "../api/api";
import { HeroGlow } from "../components/ui/hero";
import { WavesLogo } from "../components/WavesLogo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { UserAvatar } from "../components/ui/user-avatar";
import { ROLE_LABELS } from "../lib/roles";
import { getEnvironment } from "../lib/env";
import { cn } from "@/lib/utils";

const environment = getEnvironment();

function MicrosoftLogo({ size = 24 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16">
      <rect x="1" y="1" width="6.5" height="6.5" fill="#F25022"/>
      <rect x="8.5" y="1" width="6.5" height="6.5" fill="#7FBA00"/>
      <rect x="1" y="8.5" width="6.5" height="6.5" fill="#00A4EF"/>
      <rect x="8.5" y="8.5" width="6.5" height="6.5" fill="#FFB900"/>
    </svg>
  );
}

const ERROR_MESSAGES: Record<string, string> = {
  not_whitelisted: "Je e-mailadres is niet geregistreerd. Neem contact op met je beheerder.",
  invalid_state: "Je inlogpoging is verlopen of in een andere browser gestart. Probeer opnieuw in te loggen.",
  account_mismatch: "Dit e-mailadres is al gekoppeld aan een ander Microsoft-account. Neem contact op met je beheerder.",
  email_unverified: "Je organisatie heeft je e-mailadres niet geverifieerd bij Microsoft. Log in met een inloglink per e-mail, of neem contact op met je beheerder.",
  account_not_supported: "Met dit Microsoft-account kun je niet inloggen. Gebruik je werk- of schoolaccount, of log in met een inloglink per e-mail.",
};

// Only allow a single-leading-slash relative path, rejecting protocol-relative
// ("//evil.com") or absolute URLs, so the ?redirect param can't be used as an
// open redirect.
export function sanitizeRedirect(raw: string | null): string {
  if (!raw || raw.length < 2 || raw[0] !== "/" || raw[1] === "/" || raw[1] === "\\") return "/";
  return raw;
}

// For users whose IT blocks signing in to the Waves Microsoft tenant: mails
// a one-time login link. The backend answers the same whether or not the
// address is registered, so the confirmation is deliberately non-committal.
function EmailLinkLogin({ redirectTarget }: { redirectTarget: string }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [error, setError] = useState("");

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    setError("");
    try {
      const res = await fetch("/api/auth/email-link/request", {
        method: "POST", credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), redirect: redirectTarget }),
      });
      if (res.ok) setSentTo(email.trim());
      else if (res.status === 429) setError("Te veel pogingen. Wacht een minuut en probeer het opnieuw.");
      else setError("Vul een geldig e-mailadres in.");
    } catch {
      setError("Er ging iets mis. Probeer het opnieuw.");
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="self-center text-sm text-waves-muted underline underline-offset-4 hover:text-waves-ink"
      >
        Lukt inloggen met Microsoft niet? Ontvang een inloglink per e-mail
      </button>
    );
  }

  if (sentTo) {
    return (
      <div role="status" className="rounded-card border border-waves-line bg-white px-5 py-4 text-sm leading-relaxed text-waves-body">
        Als <b className="text-waves-ink">{sentTo}</b> bij ons bekend is, ontvang je binnen een paar minuten een inloglink.
        De link is 15 minuten geldig en werkt één keer.{" "}
        <button type="button" onClick={() => { setSentTo(null); setEmail(""); }} className="underline underline-offset-4 hover:text-waves-ink">
          Ander e-mailadres
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <div className="h-px flex-1 bg-waves-line" />
        <div className="text-xs text-waves-muted">of met een inloglink per e-mail</div>
        <div className="h-px flex-1 bg-waves-line" />
      </div>
      <form onSubmit={handleSubmit} className="flex gap-2">
        <Input
          type="email"
          autoComplete="email"
          autoFocus
          value={email}
          onChange={e => { setEmail(e.target.value); setError(""); }}
          placeholder="naam@bedrijf.nl"
          disabled={submitting}
          aria-invalid={!!error}
          aria-label="E-mailadres"
          className={cn("flex-1 rounded-pill px-5", error && "border-status-alert-fg")}
        />
        <Button type="submit" variant="ink" disabled={submitting || !email.trim()}>
          {submitting ? "…" : "Stuur link"}
        </Button>
      </form>
      {error && <div className="text-sm font-medium text-status-alert-fg">{error}</div>}
    </div>
  );
}

export default function Login() {
  const { isAuthenticated, isLoading, refreshSession } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();

  const [demoLoading, setDemoLoading] = useState<string | null>(null);
  const [demoCode, setDemoCode] = useState("");
  const [demoCodeError, setDemoCodeError] = useState("");
  const [demoCodeSubmitting, setDemoCodeSubmitting] = useState(false);
  const [demoUsers, setDemoUsers] = useState<DemoUser[] | null>(null);

  const { data: demoStatus } = useDemoStatus();
  const demoMode = demoStatus?.demo === true;

  useEffect(() => {
    if (!demoMode) return;
    fetch("/api/auth/demo-users", { credentials: "same-origin", headers: { "X-Demo-Secret": "" } })
      .then(res => res.ok ? res.json() : null)
      .then(users => { if (users) setDemoUsers(users); })
      .catch(() => {});
  }, [demoMode]);

  async function handleDemoCodeSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!demoCode.trim()) return;
    setDemoCodeSubmitting(true);
    setDemoCodeError("");
    try {
      const res = await fetch("/api/auth/demo-users", {
        credentials: "same-origin",
        headers: { "X-Demo-Secret": demoCode },
      });
      if (res.ok) {
        setDemoUsers(await res.json());
      } else if (res.status === 403) {
        setDemoCodeError("Ongeldige demo code");
      }
    } finally {
      setDemoCodeSubmitting(false);
    }
  }

  const urlError = searchParams.get("error") ?? "";
  const urlErrorMsg = ERROR_MESSAGES[urlError] ?? (urlError ? urlError : "");
  const redirectTarget = sanitizeRedirect(searchParams.get("redirect"));

  useEffect(() => {
    if (!isLoading && isAuthenticated) navigate(redirectTarget, { replace: true });
  }, [isAuthenticated, isLoading, navigate, redirectTarget]);

  async function handleDemoLogin(demoEmail: string) {
    setDemoLoading(demoEmail);
    setDemoCodeError("");
    try {
      const res = await fetch("/api/auth/demo-login", {
        method: "POST", credentials: "same-origin",
        headers: { "Content-Type": "application/json", "X-Demo-Secret": demoCode },
        body: JSON.stringify({ email: demoEmail }),
      });
      if (res.ok) {
        // Demo login swaps the session without a full page reload, unlike
        // real login (Microsoft OAuth redirect / magic link), which always
        // goes through the browser. The React Query cache (App.tsx's
        // module-level queryClient) is keyed without any per-user
        // dimension, so without clearing it here, every query a previous
        // demo account had cached (tasks, notifications, vacatures, ...)
        // renders stale — showing the PREVIOUS account's data — the instant
        // a component mounts under the new session, until its next refetch.
        queryClient.clear();
        await refreshSession();
        navigate(redirectTarget, { replace: true });
      }
      else if (res.status === 403) { setDemoCodeError("Ongeldige demo code"); }
    } finally { setDemoLoading(null); }
  }

  return (
    <div className="relative flex min-h-screen flex-col items-center overflow-hidden bg-gradient-to-b from-white to-waves-subtle px-6 py-10 text-waves-ink md:px-16">
      <HeroGlow />

      <div className="relative z-[1] flex items-center gap-5 self-stretch">
        <WavesLogo className="h-[34px]" />
        <div className="h-[30px] w-px bg-waves-line" />
        <span className="text-[15px] font-black tracking-[-0.02em]">Partnerportaal</span>
        {environment === "local" && (
          <span className="rounded-pill bg-status-alert-bg px-2.5 py-1 text-[11px] font-bold uppercase tracking-label text-status-alert-fg">
            Dev
          </span>
        )}
        {environment === "test" && (
          <span className="rounded-pill bg-status-warn-bg px-2.5 py-1 text-[11px] font-bold uppercase tracking-label text-status-warn-fg">
            Test
          </span>
        )}
      </div>

      <div className="relative z-[1] my-auto flex w-full max-w-[440px] flex-col gap-6 py-11">
        <div className="flex flex-col gap-4">
          <h1 className="text-[36px] font-black leading-[1.06] tracking-[-0.025em] md:text-[44px]">
            Welkom terug<br />bij <span className="text-waves-accent">Waves Network</span>.
          </h1>
          <p className="text-[15px] leading-[1.65] text-waves-body">
            Jouw openstaande vacatures, altijd actueel. Daar werken we samen aan.{" "}
            <b className="text-waves-ink">Transparant</b>, <b className="text-waves-ink">direct</b>,{" "}
            <b className="text-waves-ink">overzichtelijk</b>.
          </p>
        </div>

        {urlErrorMsg && (
          <div role="alert" className="rounded-card bg-status-alert-bg px-5 py-3.5 text-sm font-medium text-status-alert-fg">
            {urlErrorMsg}
          </div>
        )}

        <button
          type="button"
          onClick={() => { window.location.href = "/api/auth/signin/microsoft?redirect=" + encodeURIComponent(redirectTarget); }}
          className="flex items-center justify-center gap-3 rounded-pill border border-waves-ink bg-white p-[15px] text-[15px] font-bold text-waves-ink transition-colors hover:bg-waves-mist"
        >
          <MicrosoftLogo size={17} /> Inloggen met Microsoft
        </button>

        <EmailLinkLogin redirectTarget={redirectTarget} />

        {demoMode && (
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-3">
              <div className="h-px flex-1 bg-waves-line" />
              <div className="text-xs text-waves-muted">of met een demo-account</div>
              <div className="h-px flex-1 bg-waves-line" />
            </div>

            {demoUsers === null ? (
              <form onSubmit={handleDemoCodeSubmit} className="flex gap-2">
                <Input
                  type="password"
                  value={demoCode}
                  onChange={e => { setDemoCode(e.target.value); setDemoCodeError(""); }}
                  placeholder="Demo toegangscode"
                  disabled={demoCodeSubmitting}
                  aria-invalid={!!demoCodeError}
                  className={cn("flex-1 rounded-pill px-5", demoCodeError && "border-status-alert-fg")}
                />
                <Button type="submit" variant="ink" disabled={demoCodeSubmitting || !demoCode.trim()}>
                  {demoCodeSubmitting ? "…" : "Bevestigen"}
                </Button>
              </form>
            ) : (
              <div className="flex max-h-80 flex-col gap-1 overflow-y-auto rounded-card border border-waves-line bg-white p-2">
                {demoUsers.map(u => (
                  <button
                    key={u.email}
                    type="button"
                    onClick={() => handleDemoLogin(u.email)}
                    disabled={demoLoading !== null}
                    className="flex items-center gap-3 rounded-nav px-3 py-2 text-left text-[15px] transition-colors hover:bg-waves-mist disabled:opacity-50"
                  >
                    <UserAvatar name={u.name} size={34} />
                    <span className="min-w-0 flex-1 truncate font-medium">
                      {u.name} <span className="text-waves-muted">· {ROLE_LABELS[u.role] ?? u.role}</span>
                    </span>
                    {demoLoading === u.email
                      ? <span className="text-sm text-waves-muted">…</span>
                      : <ChevronRight size={16} className="text-waves-muted" />}
                  </button>
                ))}
              </div>
            )}
            {demoCodeError && <div className="text-sm font-medium text-status-alert-fg">{demoCodeError}</div>}
          </div>
        )}
      </div>

      <div className="relative z-[1] flex items-center justify-between gap-6 self-stretch text-[13px] text-waves-muted">
        <span>wavesnetwork.com</span>
        <span>~ Ontdek de kracht van het netwerk</span>
      </div>
    </div>
  );
}
